import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { formatWalletBalanceMinor } from '../../services/channelService';
import {
  getWallet,
  listCreditIssueRequests,
  postCreditIssueRequest,
} from '../../services/walletPublicService';
import { getAxiosErrorMessage, getWelcomeGreeting, logoutRemote } from '../../services/authService';
import { parseInrMajorToMinor } from '../../utils/walletTransfer';
import {
  amountMinorFromRow,
  repaymentDateFromRow,
  requestIdFromRow,
  requestorNoteFromRow,
  requestorNotePreview,
  requestedByFromRow,
  reviewNotesFromRow,
  reviewNotesPreview,
  rrnFromRow,
  statusFromRow,
  truncateMiddle,
} from '../../utils/creditIssueRequestRows';
import { useToast } from '../../context/ToastContext';

const DEFAULT_LIMIT = 20;

function RepaymentRequestPage() {
  const { walletId: walletIdParam } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [loggingOut, setLoggingOut] = useState(false);
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState(null);
  const [walletSummary, setWalletSummary] = useState(null);

  const [amountInr, setAmountInr] = useState('');
  const [rrn, setRrn] = useState('');
  const [repaymentDateLocal, setRepaymentDateLocal] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [statusFilter, setStatusFilter] = useState('');
  const [listPage, setListPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [listRows, setListRows] = useState([]);
  const [listTotal, setListTotal] = useState(0);

  const walletId = walletIdParam?.trim() ?? '';

  const loadWallet = useCallback(async () => {
    if (!walletId) return;
    setWalletLoading(true);
    setWalletError(null);
    try {
      const data = await getWallet(walletId);
      setWalletSummary(data && typeof data === 'object' ? data : null);
    } catch (e) {
      console.error(e);
      setWalletSummary(null);
      setWalletError(getAxiosErrorMessage(e, 'Could not load wallet'));
    } finally {
      setWalletLoading(false);
    }
  }, [walletId]);

  useEffect(() => {
    void loadWallet();
  }, [loadWallet]);

  const isCash =
    walletSummary &&
    typeof walletSummary.currency === 'string' &&
    walletSummary.currency.trim().toUpperCase() === 'CASH';

  const rowCurrency =
    walletSummary && typeof walletSummary.currency === 'string' && walletSummary.currency.trim() !== ''
      ? walletSummary.currency.trim()
      : 'INR';

  const loadRequests = useCallback(async () => {
    if (!walletId || !isCash) return;
    setListLoading(true);
    setListError(null);
    try {
      const params = {
        page: listPage,
        limit: DEFAULT_LIMIT,
        ...(statusFilter && ['PENDING', 'APPROVED', 'REJECTED'].includes(statusFilter) ? { status: statusFilter } : {}),
      };
      const res = await listCreditIssueRequests(walletId, params);
      if (res && typeof res === 'object' && res.success === false) {
        setListRows([]);
        setListTotal(0);
        setListError(typeof res.message === 'string' ? res.message : 'Failed to load requests');
        return;
      }
      const requests = Array.isArray(res?.requests) ? res.requests : [];
      setListRows(requests);
      setListTotal(Number(res?.total) || 0);
    } catch (e) {
      console.error(e);
      setListRows([]);
      setListTotal(0);
      setListError(getAxiosErrorMessage(e, 'Failed to load requests'));
    } finally {
      setListLoading(false);
    }
  }, [walletId, isCash, listPage, statusFilter]);

  useEffect(() => {
    if (isCash) void loadRequests();
  }, [isCash, loadRequests]);

  const totalPages = useMemo(() => {
    if (listTotal <= 0) return 1;
    return Math.max(1, Math.ceil(listTotal / DEFAULT_LIMIT));
  }, [listTotal]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logoutRemote();
    } finally {
      setLoggingOut(false);
      navigate('/login', { replace: true });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!walletId) return;
    const amt = parseInrMajorToMinor(amountInr, { allowZero: false });
    if (!amt.ok) {
      toast.error(amt.message);
      return;
    }
    const r = rrn.trim();
    if (!r) {
      toast.error('RRN is required.');
      return;
    }
    let repayment_date;
    if (repaymentDateLocal.trim()) {
      const ms = new Date(repaymentDateLocal).getTime();
      if (!Number.isFinite(ms)) {
        toast.error('Repayment date is invalid.');
        return;
      }
      repayment_date = ms;
    }
    const notesNorm = notes.trim();
    setSubmitting(true);
    try {
      const body = {
        amount_minor: amt.value,
        rrn: r,
        ...(repayment_date !== undefined ? { repayment_date } : {}),
        ...(notesNorm ? { notes: notesNorm } : {}),
      };
      const res = await postCreditIssueRequest(walletId, body);
      const ok =
        res &&
        typeof res === 'object' &&
        (res.success === true || (typeof res.request_id === 'string' && res.request_id.trim() !== ''));
      if (ok) {
        const rid = typeof res.request_id === 'string' ? res.request_id : '';
        toast.success(rid ? `Request submitted. ID: ${truncateMiddle(rid, 10, 8)}` : 'Request submitted.');
        setAmountInr('');
        setRrn('');
        setRepaymentDateLocal('');
        setNotes('');
        setListPage(1);
        void loadRequests();
        void loadWallet();
      } else {
        toast.error(typeof res?.message === 'string' ? res.message : 'Request did not succeed.');
      }
    } catch (err) {
      console.error(err);
      toast.error(getAxiosErrorMessage(err, 'Failed to submit request'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <header className="mb-8">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600">{getWelcomeGreeting()}</p>
          <button
            type="button"
            onClick={handleLogout}
            disabled={loggingOut}
            className="btn-secondary inline-flex items-center gap-2 text-sm"
          >
            <i className={`material-icons-outlined text-base ${loggingOut ? 'animate-spin' : ''}`}>
              {loggingOut ? 'refresh' : 'logout'}
            </i>
            {loggingOut ? 'Signing out…' : 'Log out'}
          </button>
        </div>
        <Link
          to={walletId ? `/wallets/${encodeURIComponent(walletId)}/statement` : '/wallets'}
          className="link-back mb-2"
        >
          <i className="material-icons-outlined text-base">arrow_back</i>
          {walletId ? 'Statement' : 'All wallets'}
        </Link>
        <div className="flex gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 text-white shadow-lg shadow-amber-500/25">
            <i className="material-icons-outlined text-3xl">request_quote</i>
          </div>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-800">Cash credit</p>
            <h1 className="page-title">Repayment request</h1>
            <p className="page-subtitle">
              Submit a credit repayment request for your cash-credit wallet. Include your bank / UPI RRN and amount. An
              operator will review and approve or reject the request.
            </p>
            {walletId ? (
              <p className="mt-1 font-mono text-xs text-slate-600 break-all">
                Wallet: <span className="text-slate-900">{walletId}</span>
              </p>
            ) : null}
          </div>
        </div>
      </header>

      {walletLoading && (
        <div className="card-surface p-8">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        </div>
      )}

      {!walletLoading && walletError && (
        <div className="card-surface p-6">
          <div className="flex items-start gap-3 border-l-4 border-red-500 bg-red-50 p-4 text-red-800">
            <i className="material-icons-outlined shrink-0">error_outline</i>
            <div>
              <p className="font-medium">Could not load wallet</p>
              <p className="mt-1 text-sm opacity-90">{walletError}</p>
            </div>
          </div>
        </div>
      )}

      {!walletLoading && !walletError && walletSummary && !isCash && (
        <div className="card-surface p-6">
          <div className="flex items-start gap-3 border-l-4 border-amber-500 bg-amber-50 p-4 text-amber-950">
            <i className="material-icons-outlined shrink-0">info</i>
            <div>
              <p className="font-medium">Repayment requests are for cash-credit wallets only</p>
              <p className="mt-1 text-sm opacity-90">
                This wallet&apos;s currency is not CASH. Open a cash-credit wallet from your dashboard to submit a
                repayment request.
              </p>
              <Link to="/wallets" className="mt-3 inline-flex text-sm font-medium text-primary-700 hover:underline">
                Back to your wallets
              </Link>
            </div>
          </div>
        </div>
      )}

      {!walletLoading && !walletError && isCash && (
        <>
          <div className="card-surface mb-6 p-6">
            <h2 className="text-lg font-semibold text-slate-900">New request</h2>
            <p className="mt-1 text-sm text-slate-600">
              Amount and RRN are required. Optional repayment date and notes help reviewers verify your submission.
            </p>
            <form className="mt-4 flex flex-col gap-3" onSubmit={submit}>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-slate-600">Amount (INR)</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={amountInr}
                  onChange={(e) => setAmountInr(e.target.value)}
                  placeholder="e.g. 10000.50"
                  className="rounded-lg border border-slate-200 px-3 py-2 font-mono text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  autoComplete="off"
                  required
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-slate-600">RRN</span>
                <input
                  type="text"
                  value={rrn}
                  onChange={(e) => setRrn(e.target.value)}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  required
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-slate-600">Repayment date and time (optional)</span>
                <input
                  type="datetime-local"
                  value={repaymentDateLocal}
                  onChange={(e) => setRepaymentDateLocal(e.target.value)}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-slate-600">Notes (optional)</span>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </label>
              <div className="mt-2 flex flex-wrap justify-end gap-2">
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit request'}
                </button>
              </div>
            </form>
          </div>

          <div className="card-surface overflow-hidden p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Your requests</h2>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                Status
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setListPage(1);
                  }}
                  className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-900"
                >
                  <option value="">All</option>
                  <option value="PENDING">PENDING</option>
                  <option value="APPROVED">APPROVED</option>
                  <option value="REJECTED">REJECTED</option>
                </select>
              </label>
            </div>

            {listError && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                <i className="material-icons-outlined shrink-0 text-base">error_outline</i>
                <span>{listError}</span>
              </div>
            )}

            <div className="mt-4 overflow-x-auto rounded-lg border border-slate-100">
              <table className="min-w-full divide-y divide-slate-100 text-sm">
                <thead>
                  <tr className="bg-slate-50/90 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="whitespace-nowrap px-3 py-3">Request ID</th>
                    <th className="whitespace-nowrap px-3 py-3 text-right">Amount</th>
                    <th className="whitespace-nowrap px-3 py-3">RRN</th>
                    <th className="whitespace-nowrap px-3 py-3">Status</th>
                    <th className="whitespace-nowrap px-3 py-3">Requested by</th>
                    <th className="whitespace-nowrap px-3 py-3">Repayment date</th>
                    <th className="min-w-[8rem] px-3 py-3">Requestor note</th>
                    <th className="min-w-[8rem] px-3 py-3">Reviewer notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {listLoading && (
                    <tr>
                      <td colSpan={8} className="px-3 py-10 text-center text-slate-500">
                        <i className="material-icons-outlined mr-1 inline-block animate-spin align-middle text-base">
                          refresh
                        </i>{' '}
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!listLoading && listRows.length === 0 && !listError && (
                    <tr>
                      <td colSpan={8} className="px-3 py-10 text-center text-slate-500">
                        No requests yet for this wallet.
                      </td>
                    </tr>
                  )}
                  {!listLoading &&
                    listRows.map((row, index) => {
                      const rid = requestIdFromRow(row);
                      const st = statusFromRow(row);
                      const badgeClass =
                        st === 'APPROVED'
                          ? 'bg-emerald-100 text-emerald-900'
                          : st === 'REJECTED'
                            ? 'bg-red-100 text-red-900'
                            : st === 'PENDING'
                              ? 'bg-amber-100 text-amber-900'
                              : 'bg-slate-100 text-slate-800';
                      const minor = amountMinorFromRow(row);
                      return (
                        <tr key={rid || String(index)} className="hover:bg-slate-50/80">
                          <td className="max-w-[14rem] px-3 py-2.5 font-mono text-xs text-slate-800" title={rid || ''}>
                            {rid ? truncateMiddle(rid) : '—'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-right font-semibold tabular-nums text-slate-900">
                            {minor != null ? formatWalletBalanceMinor(minor, rowCurrency) : '—'}
                          </td>
                          <td className="max-w-[10rem] break-all px-3 py-2.5 font-mono text-xs text-slate-700">
                            {rrnFromRow(row)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass}`}>
                              {st || '—'}
                            </span>
                          </td>
                          <td className="max-w-[10rem] break-all px-3 py-2.5 font-mono text-xs text-slate-700">
                            {requestedByFromRow(row)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-slate-700">{repaymentDateFromRow(row)}</td>
                          <td
                            className="max-w-xs break-words px-3 py-2.5 text-xs text-slate-700"
                            title={requestorNoteFromRow(row) || undefined}
                          >
                            {requestorNotePreview(row)}
                          </td>
                          <td
                            className="max-w-xs break-words px-3 py-2.5 text-xs text-slate-700"
                            title={reviewNotesFromRow(row) || undefined}
                          >
                            {reviewNotesPreview(row)}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
              <span>
                {listTotal > 0 ? (
                  <>
                    Page {listPage} of {totalPages} ({listTotal} request{listTotal === 1 ? '' : 's'})
                  </>
                ) : (
                  <>No pagination</>
                )}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="btn-secondary py-1 pl-2 pr-2.5 text-xs"
                  disabled={listLoading || listPage <= 1}
                  onClick={() => setListPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn-secondary py-1 pl-2 pr-2.5 text-xs"
                  disabled={listLoading || listPage >= totalPages}
                  onClick={() => setListPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default RepaymentRequestPage;
